export type CategoriaPremioId =
  | 'ELBB'
  | 'CONEXAO'
  | 'PILAR'
  | 'SIMPLIFIQUE'
  | 'ECBB_ASG'
  | 'ECBB_CLIENTES'
  | 'ECBB_FINANCEIRO'
  | 'ECBB_TDIGITAL'
  | 'ECBB_CULTURA_PESSOAS';

const CATEGORY_ALIASES: Record<string, CategoriaPremioId> = {
  ELBB: 'ELBB',
  CONEXAO: 'CONEXAO',
  NEGOCIOS: 'CONEXAO',
  PILAR: 'PILAR',
  SUSTENTACAO: 'PILAR',
  SIMPLIFIQUE: 'SIMPLIFIQUE',
  SIMPLIFICACAO: 'SIMPLIFIQUE',
  ECBB_ASG: 'ECBB_ASG',
  ECBB_CLIENTES: 'ECBB_CLIENTES',
  ECBB_CLIENTE: 'ECBB_CLIENTES',
  ECBB_FINANCEIRO: 'ECBB_FINANCEIRO',
  ECBB_TDIGITAL: 'ECBB_TDIGITAL',
  ECBB_TRANSFORMACAO: 'ECBB_TDIGITAL',
  ECBB_CULTURA_PESSOAS: 'ECBB_CULTURA_PESSOAS',
  ECBB_CULTURA: 'ECBB_CULTURA_PESSOAS',
  'PRÊMIO ELBB': 'ELBB',
  'PREMIO ELBB': 'ELBB',
};

const CATEGORY_LABELS: Record<CategoriaPremioId, string> = {
  ELBB: 'Prêmio ELBB',
  CONEXAO: 'Negócios e Atendimento',
  PILAR: 'Pilar de Sustentação',
  SIMPLIFIQUE: 'Simplificação e Eficiência',
  ECBB_ASG: 'ECBB - ASG',
  ECBB_CLIENTES: 'ECBB - Clientes',
  ECBB_FINANCEIRO: 'ECBB - Financeiro',
  ECBB_TDIGITAL: 'ECBB - Transformação digital e Processos',
  ECBB_CULTURA_PESSOAS: 'ECBB - Cultura e Pessoas',
};

const CATEGORY_COLORS: Record<CategoriaPremioId, string> = {
  ELBB: 'green-bb',
  CONEXAO: 'blue-bb-6',
  PILAR: 'yellow-bb',
  SIMPLIFIQUE: 'pink-bb',
  ECBB_ASG: 'blue-bb-6',
  ECBB_CLIENTES: 'blue-bb-6',
  ECBB_FINANCEIRO: 'blue-bb-6',
  ECBB_TDIGITAL: 'blue-bb-6',
  ECBB_CULTURA_PESSOAS: 'blue-bb-6',
};

const CATEGORY_HEX_COLORS: Record<CategoriaPremioId, string> = {
  ELBB: '#4DB6AC',
  CONEXAO: '#9187AF',
  PILAR: '#B47D7D',
  SIMPLIFIQUE: '#4BBBD5',
  ECBB_ASG: '#5AAAA0',
  ECBB_CLIENTES: '#465EFF',
  ECBB_FINANCEIRO: '#FE6E91',
  ECBB_TDIGITAL: '#FF9763',
  ECBB_CULTURA_PESSOAS: '#589BF7',
};

export const CATEGORIAS_INICIATIVAS_OFICIAIS: Array<{ id: CategoriaPremioId; label: string }> = [
  { id: 'CONEXAO', label: CATEGORY_LABELS.CONEXAO },
  { id: 'PILAR', label: CATEGORY_LABELS.PILAR },
  { id: 'SIMPLIFIQUE', label: CATEGORY_LABELS.SIMPLIFIQUE },
  { id: 'ECBB_ASG', label: CATEGORY_LABELS.ECBB_ASG },
  { id: 'ECBB_CLIENTES', label: CATEGORY_LABELS.ECBB_CLIENTES },
  { id: 'ECBB_FINANCEIRO', label: CATEGORY_LABELS.ECBB_FINANCEIRO },
  { id: 'ECBB_TDIGITAL', label: CATEGORY_LABELS.ECBB_TDIGITAL },
  { id: 'ECBB_CULTURA_PESSOAS', label: CATEGORY_LABELS.ECBB_CULTURA_PESSOAS },
];

export const normalizeCategoryPremio = (categoryPremio?: string): string => {
  if (!categoryPremio) return '';
  const normalized = categoryPremio.trim().toUpperCase();
  return CATEGORY_ALIASES[normalized] || normalized;
};

export const getCategoriaLabel = (categoryPremio?: string): string => {
  const normalizedId = normalizeCategoryPremio(categoryPremio);
  if (!normalizedId) return '';
  return CATEGORY_LABELS[normalizedId as CategoriaPremioId] || normalizedId;
};

export const getCategoriaCor = (categoryPremio?: string): string => {
  const normalizedId = normalizeCategoryPremio(categoryPremio);
  if (!normalizedId) return 'blue-bb-6';
  return CATEGORY_COLORS[normalizedId as CategoriaPremioId] || 'blue-bb-6';
};

export const getCategoriaCorHex = (categoryPremio?: string): string => {
  const normalizedId = normalizeCategoryPremio(categoryPremio);
  if (!normalizedId) return '#465EFF';
  return CATEGORY_HEX_COLORS[normalizedId as CategoriaPremioId] || '#465EFF';
};

export const isCategoriaElbb = (categoryPremio?: string): boolean => {
  return normalizeCategoryPremio(categoryPremio) === 'ELBB';
};

export interface ResultadoLabel {
  titulo: string;
  mostrar: boolean;
}

export function getResultadoLabel(
  categoryPremio?: string,
  indicator?: { nome?: string; perspectiva?: string },
  resultText?: string
): ResultadoLabel {
  const cat = normalizeCategoryPremio(categoryPremio);
  switch (cat) {
    case 'CONEXAO':
      return {
        titulo: `Por que essa iniciativa negocial foi essencial para a sustentação e a evolução do Banco, contribuindo para expressar o Propósito do BB, bem como para a superação do indicador da Plataforma Conexão, "${indicator?.nome ?? '—'}", ao qual está vinculada?`,
        mostrar: true,
      };
    case 'PILAR':
      return {
        titulo: `Por que essa iniciativa estruturante foi essencial para a sustentação e a evolução do Banco, contribuindo para expressar o Propósito do BB, bem como para a superação do indicador da Plataforma Conexão do Pilar de Sustentação, "${indicator?.nome ?? '—'}", ao qual está vinculada?`,
        mostrar: true,
      };
    case 'ELBB':
      return {
        titulo: `Por que essa iniciativa foi determinante para o cumprimento da perspectiva da ECBB, "${indicator?.perspectiva ?? '—'}", à qual está vinculada e quais resultados foram obtidos?`,
        mostrar: true,
      };
    case 'SIMPLIFIQUE':
      return {
        titulo: `Por que essa iniciativa foi fundamental para o cumprimento do direcionador estratégico ao qual está vinculado e quais resultados foram obtidos?`,
        mostrar: false,
      };
    case 'ECBB_ASG':
    case 'ECBB_CLIENTES':
    case 'ECBB_FINANCEIRO':
    case 'ECBB_TDIGITAL':
    case 'ECBB_CULTURA_PESSOAS':
      return {
        titulo: `Por que essa iniciativa foi fundamental para o cumprimento do direcionador estratégico ao qual está vinculado, "${indicator?.nome ?? '—'}", e quais resultados foram obtidos?`,
        mostrar: true,
      };
    default:
      return {
        titulo: `Por que essa iniciativa foi fundamental para o cumprimento do direcionador estratégico ao qual está vinculado e quais resultados foram obtidos?`,
        mostrar: true,
      };
  }
}